import { useEffect, useRef, useState, type CSSProperties } from 'react';
import type { HeroTabCopy } from '@/copy/hero-en';

interface TabRowProps {
  tabs: readonly HeroTabCopy[];
  activeIndex: number;
  onSelect: (index: number) => void;
  showProgress?: boolean;
  paused?: boolean;
  /**
   * False while the carousel has not begun — the demo is not on screen yet.
   * Freezes the progress fill WITHOUT showing the pause badge: a bar counting
   * down a dwell that is not elapsing is a lie, and so is a pause glyph on
   * something that was never running. Defaults true so every other caller and
   * every story keeps the old behaviour.
   */
  started?: boolean;
  cycleMs?: number;
}

export default function TabRow({
  tabs,
  activeIndex,
  onSelect,
  showProgress = false,
  paused = false,
  started = true,
  cycleMs = 6000,
}: TabRowProps) {
  const listRef = useRef<HTMLDivElement>(null);
  const [indicatorStyle, setIndicatorStyle] = useState<CSSProperties>({});
  /* The pause badge gets its OWN placement, not the indicator's — see measure. */
  const [pauseStyle, setPauseStyle] = useState<CSSProperties>({});
  const [dragging, setDragging] = useState(false);

  useEffect(() => {
    const list = listRef.current;
    if (!list) return;

    const measure = () => {
      const active = list.children[activeIndex];
      if (!(active instanceof HTMLElement)) return;
      const parentRect = list.getBoundingClientRect();
      const elRect = active.getBoundingClientRect();
      const isRtl = getComputedStyle(list).direction === 'rtl';
      /* BUGFIX 2026-09-05: `scrollLeft` was missing, so once the narrow row had
         scrolled, the bar sat exactly scrollLeft px behind its tab — most
         visibly on the LAST tab, where the auto-scroll has just run and the bar
         ended up marking the tab before it.
         The indicator is absolutely positioned inside the row, which is the
         scroll container, so its origin is the CONTENT box and scrolls with it.
         getBoundingClientRect returns viewport coordinates, which do not. Adding
         the signed scrollLeft converts one to the other, and the result is
         scroll-invariant — no re-measure on scroll needed. */
      const scroll = list.scrollLeft;
      const offset = isRtl
        ? parentRect.right - elRect.right - scroll
        : elRect.left - parentRect.left + scroll;
      setIndicatorStyle({
        transform: `translateX(${isRtl ? -offset : offset}px)`,
        width: `${elRect.width}px`,
      });

      /* BUGFIX 2026-09-06 — the pause badge is placed SEPARATELY, on a whole
         pixel. It used to share indicatorStyle and centre itself in that box
         with flex, which put its 14px disc at whatever fractional x the tab
         happened to sit at: measured .359 / .406 / .016 / .547 / .578 across
         the five tabs. Chrome then snapped the disc's background strokes by
         that fraction, so the glyph leaned a different way on each tab (owner:
         "its location differs between each tab") — tab 2 leaned left while the
         rest leaned right.

         So this is a zero-WIDTH marker parked at the tab's centre rounded to an
         integer SCREEN coordinate, and the disc overflows it by -7px/+7px under
         the same flex centring. Screen space, not offset space: an integer
         offset added to a fractional parent origin is still fractional.
         Rounding costs at most half a pixel against a ~140px tab, which is
         nothing; not rounding costs a badge that visibly moves. */
      const snapped = Math.round(elRect.left + elRect.width / 2);
      const pauseOffset = isRtl
        ? parentRect.right - snapped - scroll
        : snapped - parentRect.left + scroll;
      setPauseStyle({
        transform: `translateX(${isRtl ? -pauseOffset : pauseOffset}px)`,
        width: 0,
      });
    };

    measure();

    /* BUGFIX 2026-09-05: measuring once left the bar 3.28px wider than the tab
       it marks, at every breakpoint. The first measurement runs before IBM Plex
       Sans arrives, so it captures the FALLBACK font's text width and never
       looks again — the delta was constant because the label is the same in
       every case. Two re-measures fix it: the font swap, and any later resize
       (breakpoint change, zoom, scrollbar appearing). */
    let ro: ResizeObserver | undefined;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(measure);
      ro.observe(list);
    }
    // Not all test environments implement document.fonts.
    document.fonts?.ready.then(measure).catch(() => {});

    return () => ro?.disconnect();
  }, [activeIndex, tabs]);

  /* Keep the selected tab in view in the scrolling states. Without this the
     cycle happily advances to a tab that is entirely off the right edge, and
     the visitor sees the mockup change with no visible reason.

     Deliberately NOT scrollIntoView: on a nested scroll container that also
     scrolls the PAGE to bring the row into view, which would yank the document
     every 6 seconds. Setting scrollLeft touches only the row.

     Only acts when the tab is actually clipped, so a row that fits never
     twitches. */
  useEffect(() => {
    const list = listRef.current;
    if (!list) return;

    const raf = requestAnimationFrame(() => {
      const el = list.children[activeIndex];
      if (!(el instanceof HTMLElement)) return;
      if (list.scrollWidth <= list.clientWidth + 1) return;

      const listRect = list.getBoundingClientRect();
      const elRect = el.getBoundingClientRect();
      // Treat the edge-fade band as not-visible. Read the width rather than
      // hardcoding it: --tabs-bleed steps 32 -> 24 -> 20 down the breakpoints.
      const inset =
        parseFloat(getComputedStyle(list).getPropertyValue('--tabs-bleed')) || 24;
      const clipped =
        elRect.left < listRect.left + inset || elRect.right > listRect.right - inset;
      if (!clipped) return;

      const delta =
        elRect.left + elRect.width / 2 - (listRect.left + listRect.width / 2);
      const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      list.scrollTo({
        left: list.scrollLeft + delta,
        behavior: reduced ? 'auto' : 'smooth',
      });
    });

    return () => cancelAnimationFrame(raf);
  }, [activeIndex, tabs]);

  /* Drag-to-scroll for the narrow states, where the row is wider than the
     viewport and the scrollbar is hidden by design. Touch already pans the
     overflow container natively with momentum, so this handles MOUSE only —
     claiming touch here would mean re-implementing that badly.
     A drag past the threshold swallows the click it would otherwise end in,
     so dragging past a tab never selects it. */
  useEffect(() => {
    const list = listRef.current;
    if (!list) return;

    let pointerId = -1;
    let startX = 0;
    let startScroll = 0;
    let moved = false;

    const onPointerDown = (e: PointerEvent) => {
      if (e.pointerType !== 'mouse' || e.button !== 0) return;
      if (list.scrollWidth <= list.clientWidth + 1) return;
      pointerId = e.pointerId;
      startX = e.clientX;
      startScroll = list.scrollLeft;
      moved = false;
    };

    const onPointerMove = (e: PointerEvent) => {
      if (e.pointerId !== pointerId) return;
      const dx = e.clientX - startX;
      if (!moved) {
        if (Math.abs(dx) < 4) return;
        moved = true;
        setDragging(true);
        list.setPointerCapture(pointerId);
      }
      list.scrollLeft = startScroll - dx;
      e.preventDefault();
    };

    const endDrag = (e: PointerEvent) => {
      if (e.pointerId !== pointerId) return;
      if (list.hasPointerCapture(pointerId)) list.releasePointerCapture(pointerId);
      pointerId = -1;
      setDragging(false);
    };

    // Capture phase, so the swallow happens before the button's own onClick.
    const onClickCapture = (e: MouseEvent) => {
      if (!moved) return;
      moved = false;
      e.preventDefault();
      e.stopPropagation();
    };

    list.addEventListener('pointerdown', onPointerDown);
    list.addEventListener('pointermove', onPointerMove);
    list.addEventListener('pointerup', endDrag);
    list.addEventListener('pointercancel', endDrag);
    list.addEventListener('click', onClickCapture, true);

    return () => {
      list.removeEventListener('pointerdown', onPointerDown);
      list.removeEventListener('pointermove', onPointerMove);
      list.removeEventListener('pointerup', endDrag);
      list.removeEventListener('pointercancel', endDrag);
      list.removeEventListener('click', onClickCapture, true);
    };
  }, []);

  return (
    /* BUGFIX 2026-09-04: this only ever set an `is-active` CLASS, but every
       selected-state rule in Hero.css keys off [aria-selected="true"]. Nothing
       set that attribute, so the active tab never took its white colour and the
       inactive-only hover underline fired on the active tab too. Setting the
       real ARIA state fixes the styling and makes the row a proper tablist. */
    <div
      className={`mkt-hero-tw__tabs${dragging ? ' is-dragging' : ''}`}
      role="tablist"
      ref={listRef}
    >
      {tabs.map((tab, i) => (
        <button
          key={i}
          type="button"
          role="tab"
          aria-selected={i === activeIndex}
          className={`mkt-hero-tw__tab${i === activeIndex ? ' is-active' : ''}`}
          onClick={() => onSelect(i)}
        >
          <span className="mkt-hero-tw__tab-verb">{tab.verb}</span>
          <span className="mkt-hero-tw__tab-noun">{tab.noun}</span>
        </button>
      ))}
      <span
        className={`mkt-hero-tw__tab-indicator${
          showProgress ? ' mkt-hero-tw__tab-indicator--progress' : ''
        }`}
        style={indicatorStyle}
        data-paused={paused}
      >
        {showProgress ? (
          /* BUGFIX 2026-09-04 — key={activeIndex}.
             The fill is `animation: tab-progress <cycle> linear forwards`. React
             kept reusing this same DOM node across tab changes, so the animation
             ran ONCE on mount and `forwards` then pinned it at scaleX(1) for
             good: every subsequent tab showed an instantly-full bar and the
             countdown looked broken. Keying on the active index remounts the
             node per tab, which restarts the animation. */
          /* The key is deliberately the index ALONE. It briefly carried
             `paused` as well, to remount the fill on the pause edge and restart
             it — matching a Hero timeout that rearmed from zero on every
             unhover. Both halves of that were the bug: the owner wants a hover
             to pause, not reset. Hero now debits the elapsed time and rearms
             for the remainder, and `animation-play-state` freezes this fill in
             place, so the two resume together from where they stopped.
             Remounting here would put the bar back to empty while the timer
             carried on, which is the same lie in the other direction. */
          <span
            key={activeIndex}
            className="mkt-hero-tw__tab-progress"
            style={{
              animationDuration: `${cycleMs}ms`,
              animationPlayState: !started || paused ? 'paused' : 'running',
            }}
          />
        ) : null}
      </span>
      {/* The pause badge. Deliberately a sibling of the indicator: the
          indicator clips its own overflow and is 3px tall, so a glyph cannot
          live inside it. It carries pauseStyle rather than indicatorStyle — a
          pixel-snapped point rather than the tab's box — because a 14px disc
          centred in a fractional box lands on a fractional pixel and its
          strokes snap differently on every tab. Both come out of the same
          measure(), so they stay in step through tab changes, resizes and RTL.
          Decorative — the carousel's state is not information a screen reader
          needs narrated mid-read. */}
      {showProgress && paused ? (
        <span
          className="mkt-hero-tw__tab-pause"
          style={pauseStyle}
          aria-hidden="true"
        >
          {/* A real element rather than a ::before, so its box can be measured.
              As a pseudo-element the only thing a probe could read was the
              wrapper, which meant assuming the disc sat centred inside it —
              and that assumption was exactly what was broken. */}
          <span className="mkt-hero-tw__tab-pause-dot" />
        </span>
      ) : null}
    </div>
  );
}
